import React, { useState, useEffect, useCallback } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { EditOutlined, DeleteOutlined, Add } from '@mui/icons-material';
import { Button, Chip, IconButton, Typography, Box, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import api from "../../services/api";
import { API_ENDPOINTS } from "../../config/api";
import { hasPermission, PERMISSIONS } from "../../utils/permissions";
import { useAuth } from "../../context/AuthContext";

const Users = () => {
  const navigate = useNavigate();
  const { user } = useAuth();  // Get the whole user object
  const userPermissions = user?.permissions;
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(5);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(API_ENDPOINTS.USER.GET_ALL, {
        params: { timestamp: new Date().getTime(), page: page + 1, limit: pageSize }
      });
      setUsers(response.data.users);
      setTotalItems(response.data.totalItems);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to fetch users');
      setLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await api.delete(API_ENDPOINTS.USER.DELETE(id));
        fetchUsers(); // Refresh the list after deletion
      } catch (err) {
        console.error('Error deleting user:', err);
        setError('Failed to delete user');
      }
    }
  };

  // Define columns with custom rendering for role and actions
  const columns = [
    { field: 'name', headerName: 'Name', flex: 0.2, minWidth: 150 },
    { field: 'email', headerName: 'Email', flex: 0.25, minWidth: 200 },
    {
      field: 'role_id',
      headerName: 'Roles',
      flex: 0.15,
      minWidth: 120,
      renderCell: (params) => (
        <Chip
          label={params.value.name}
          sx={{
            color: params.value.name === 'Admin' ? '#E31B54' : 'primary.main',
            padding: '4px 8px',
            borderRadius: '16px',
            fontWeight: 'medium',
          }}
        />
      ),
    },
    { 
      field: 'created_at', 
      headerName: 'Created at', 
      flex: 0.2, 
      minWidth: 180,
      valueGetter: (params) => {
        const date = new Date(params);
        return date instanceof Date && !isNaN(date) 
          ? date.toLocaleString('id-ID', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              hour12: true
            })
          : '-';
      }
    },
    ...(hasPermission(userPermissions, 'user', PERMISSIONS.UPDATE) || hasPermission(userPermissions, 'user', PERMISSIONS.DELETE) ? [{
      field: 'actions',
      headerName: 'Actions',
      flex: 0.1,
      minWidth: 120,
      renderCell: (params) => (
        <Box>
          {hasPermission(userPermissions, 'user', PERMISSIONS.UPDATE) && (
            <IconButton size='small' sx={{ color: '#6C757D' }} onClick={() => navigate(`/users/edit/${params.row.id}`)}>
              <EditOutlined fontSize='inherit'/>
            </IconButton>
          )}
          {hasPermission(userPermissions, 'user', PERMISSIONS.DELETE) && (
            <IconButton size='small' sx={{ color: '#E31B54' }} onClick={() => handleDelete(params.row.id)}>
              <DeleteOutlined fontSize='inherit'/>
            </IconButton>
          )}
        </Box>
      ),
    }] : [])
  ];

  if (loading) return <CircularProgress />;
  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <Box sx={{ width: '100%', pl: 1, pr: 2, pt: 0, backgroundColor: 'background.paper', borderRadius: 1, mb: 2 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 2 }}>
          Users
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          {hasPermission(userPermissions, 'user', PERMISSIONS.CREATE) && (
            <Button
              variant="contained"
              size="small"
              startIcon={<Add />}
              onClick={() => navigate('/users/create')}
              sx={{
              textTransform: 'none',
              backgroundColor: '#E31B54',
              color: '#FFFFFF',
              minWidth: '120px',
              px: 2,
              height: '32px',
              borderRadius: '16px',
              '&:hover': {
                backgroundColor: '#C41747',
              },
            }}
          >
            New user
            </Button>
          )}
        </Box>
      </Box>
      <DataGrid
        rows={users}
        columns={columns}
        pagination
        paginationMode="server"
        rowCount={totalItems}
        pageSizeOptions={[5, 10, 25]}
        paginationModel={{ page, pageSize }}
        onPaginationModelChange={(newModel) => {
          setPage(newModel.page);
          setPageSize(newModel.pageSize);
        }}
        disableRowSelectionOnClick
        getRowId={(row) => row.id}
        disableColumnMenu
        sx={{
          borderRadius: 6,
          borderColor: 'background.primary',
          boxShadow: 1,
          color: 'text.secondary',
          bgcolor: 'background.paper',
          '& .MuiDataGrid-cell:focus': {
            outline: 'none',
          },
          '& .MuiDataGrid-columnHeader': {
            '&:focus': {
              outline: 'none',
            },
            '&:focus-within': {
              outline: 'none',
            }
          },
          '& .MuiDataGrid-columnSeparator': {
            display: 'none',
          },
          '& .MuiDataGrid-cell': {
            borderBottom: '1px solid',
            borderColor: 'background.default',
          },
        }}
      />
    </Box>
  );
};

export default Users;
